import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { catchError, map, Observable, tap, of } from 'rxjs';

import {
    DateForPmis,
    Guid,
    IEntityAttribute,
    IEntityBase,
    IModelBase,
    Pmis3JsonHelper,
} from '@pmis3/core';

import { AuthConfig } from './auth-config';
import { HttpQueryResult, IQueryParam, IQueryResult, IUserData } from '../../';
import { DataAccessHelper } from './data-access-helper';
import { ICommonData, ICommonDataAttribute } from '../models/common-data';

@Injectable({ providedIn: 'root' })
export class DataAccessService {
    private get _uri(): string {
        return AuthConfig.uri;
    }
    private readonly apiGateway: string = `${this._uri}/{{0}}/v1.0/{{1}}`;

    constructor(private httpClient: HttpClient) {}

    private _getUrl(service: string, path: string): string {
        return this.apiGateway.replace('{{0}}', service).replace('{{1}}', path);
    }

    queryEntity<T extends IModelBase>(
        service: string,
        path: string,
        query?: IQueryParam
    ): Observable<IQueryResult<T>> {
        let url = this._getUrl(service, path);
        if (query) {
            url += '?t=' + new Date().getTime();
            if ((query.pageIndex ?? 0) > 0)
                url += `&pageIndex=${query.pageIndex}`;
            if ((query.pageSize ?? 0) > 0) url += `&pageSize=${query.pageSize}`;
            if (query.orderBy) {
                const orderBy = { ...query.orderBy };
                url += `&orderBy=${orderBy.field}&orderBy_desc=${
                    orderBy.desc ?? false
                }`;
            }
            if (query.params) {
                url += `&${this.joinParams(query.params)}`;
            }
        }
        return this.httpClient
            .get(url)
            .pipe(
                tap((r) => Pmis3JsonHelper.CheckJsonObject(r)),
                catchError(() => {
                    return of({
                        items: [],
                        totalCount: 0,
                    } as IQueryResult<T>);
                }),
            );
    }

    private _getHttpUrl(url: string): string {
        const root = this._uri;
        if (!url?.startsWith(root)) {
            url = root + '/' + url;
        }
        return url;
    }

    httpGet<T extends IModelBase>(url: string): Observable<HttpQueryResult<T>> {
        const url2 = this._getHttpUrl(url);
        return this.httpClient.get(url2).pipe(
            map((r) => {
                const qr = r as HttpQueryResult<T>;
                Pmis3JsonHelper.CheckJsonObject(qr);
                return qr;
            })
        );
    }

    httpPost<T extends IEntityBase>(url: string, data: T): Observable<T> {
        if ((data.attributes?.length ?? 0) > 0) {
            data.id = data.id || Guid.newGuid();
            data.attributes?.forEach((a) => {
                a.id = a.id || Guid.newGuid();
                a.entityId = data.id;
            });
        }
        return this._httpCall('post', url, data);
    }

    httpPut<T>(url: string, data: T): Observable<T> {
        return this._httpCall('put', url, data);
    }

    private _httpCall<T>(method: string, url: string, data: T): Observable<T> {
        const url2 = this._getHttpUrl(url);
        const httpData = DataAccessHelper.toHttpData(data);
        return this.httpClient.request(method, url2, { body: httpData }).pipe(
            map((r) => {
                Pmis3JsonHelper.CheckJsonObject(r);
                return r as T;
            })
        );
    }

    private joinParams(params: Map<string, string>): string {
        return Array.from(params?.entries() || [])
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
    }

    patch(service: string, method: string, data: object): Observable<any> {
        const httpData = DataAccessHelper.toHttpData(data);
        const url = this._getUrl(service, method);
        return this.httpClient
            .patch(url, httpData)
            .pipe(tap((r) => Pmis3JsonHelper.CheckJsonObject(r)));
    }

    post(service: string, method: string, data: object): Observable<any> {
        const httpData = DataAccessHelper.toHttpData(data);
        const url = this._getUrl(service, method);
        return this.httpClient
            .post(url, httpData)
            .pipe(tap((r) => Pmis3JsonHelper.CheckJsonObject(r)));
    }

    put(service: string, method: string, data: object): Observable<any> {
        const httpData = DataAccessHelper.toHttpData(data);
        const url = this._getUrl(service, method);
        return this.httpClient.put(url, httpData);
    }

    get(domain: string, action: string): Observable<any> {
        return this.httpClient.get(this._getUrl(domain, action));
    }

    queryCommonData(
        service: string,
        category: string,
        name?: string,
        refId?: Guid
    ): Observable<IQueryResult<ICommonData>> {
        const params = new Map<string, string>();
        params.set('category', category);
        if (name) {
            params.set('name', name);
        }
        if (refId) {
            params.set('refId', refId.toString());
        }
        return this.queryEntity<ICommonData>(service, 'commondata', { params });
    }

    public queryCommonDataAttributeHistory(
        service: string,
        entityId: Guid,
        attributeName: string,
        dateTimeFrom: Date,
        dateTimeTo: Date
    ): Observable<IQueryResult<ICommonDataAttribute>> {
        const params = new Map<string, string>();
        params.set('entityId', entityId.toString());
        params.set('attributeName', attributeName);
        params.set(
            'dateTimeFrom',
            new DateForPmis(dateTimeFrom).getISOString()
        );
        params.set('dateTimeTo', new DateForPmis(dateTimeTo).getISOString());
        return this.queryEntity(service, 'commondataattributehistory', {
            params,
        });
    }

    createCommonData(
        service: string,
        data: ICommonData
    ): Observable<ICommonData> {
        const url = this._getUrl(service, 'commondata');
        this._checkCommonData(data);
        return this.httpPost<ICommonData>(url, data);
    }

    updateCommonData(service: string, data: ICommonData): Observable<void> {
        const url = this._getUrl(service, 'commondata');
        this._checkCommonData(data);
        return this.httpPut<ICommonData>(url, data).pipe(map(() => void 0));
    }

    private _checkCommonData(data: ICommonData): void {
        data.id = data.id || Guid.newGuid();
        data.refId = data.refId || Guid.emptyId;
        data.attributes?.forEach((a) => {
            a.id = a.id || Guid.newGuid();
            a.entityId = data.id;
        });
    }

    public getUserData(category: string): Observable<IUserData | undefined> {
        return this.get('i', `userdata?category=${category}`);
    }

    public updateUserData(data: IUserData): Observable<IUserData | undefined> {
        return this.post('i', 'userdata', data);
    }

    public queryApiService(service: string, apiName: string, body: any | undefined = undefined): Observable<any> {
        const url = this._getUrl(service, 'api-service') + `?ServiceName=${apiName}`;
        return this.httpClient.post(url, body || {});
    }

    public queryApiServiceBlob(service: string, apiName: string, body: any | undefined = undefined): Observable<any> {
        const url = this._getUrl(service, 'api-service') + `?ServiceName=${apiName}`;
        return this.httpClient.post(url, body || {}, 
            { responseType: 'blob', observe: 'response'});
    }

    public getConfigData(service: string, name: string): Observable<IQueryResult<IEntityAttribute>> {
        const url = this._getUrl(service, `config?name=${name}`);
        return this.httpClient.get(url);
    }
}
